import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar as CalendarIcon, Plus, MapPin, Clock, Upload, ChevronLeft, ChevronRight, List, Grid, Mic, Download, FileSpreadsheet, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO, isValid } from 'date-fns';
import VoiceInput from '@/components/VoiceInput';
import usePermissions from '@/hooks/usePermissions';
import { ReadOnlyBanner, NoAccessScreen } from '@/components/PermissionUI';
import EditableImportReview from '@/components/EditableImportReview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Import review shows US formats; the backend parser accepts both these and ISO on confirm.
const toUsDate = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || '') ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : iso || '');
const fmtWhen = (sch) => {
  const d = parseISO(String(sch.date || ''));
  if (!isValid(d)) return sch.date || '';
  const hasClock = /T\d{2}:\d{2}/.test(sch.date || '');
  const clock = hasClock ? format(d, 'hh:mm a') : (sch.time ? to12h(sch.time) : '');
  return `${format(d, 'PP')}${clock ? ` · ${clock}` : ''}`;
};
const to12h = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return hhmm || '';
  const h = Number(m[1]);
  return `${String(h % 12 || 12).padStart(2, '0')}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
};

export default function SchedulePage({ user, onLogout, permissions }) {
  const { canEdit, canView, isReadOnly } = usePermissions(permissions);
  const [showImportReview, setShowImportReview] = useState(false);
  const [importPreviewRows, setImportPreviewRows] = useState([]);
  const [importSourceHeaders, setImportSourceHeaders] = useState([]);
  const [importRawRows, setImportRawRows] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [schedules, setSchedules] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [clearSchedule, setClearSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    team_id: user.team_id,
    type: 'practice',
    title: '',
    date: '',
    location: '',
    opponent: '',
    notes: '',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_end_date: ''
  });

  useEffect(() => {
    loadSchedules();
    // Check URL params to auto-open add dialog
    if (searchParams.get('action') === 'add') {
      setShowAddDialog(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const loadSchedules = async () => {
    try {
      const response = await axios.get(`${API}/schedules?team_id=${user.team_id}`);
      setSchedules(response.data.sort((a, b) => new Date(a.date) - new Date(b.date)));
    } catch (error) {
      toast.error('Failed to load schedules');
    }
  };

  const deleteSchedule = async (schedule) => {
    if (!window.confirm(`Delete "${schedule.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/schedules/${schedule.id}`);
      toast.success('Event deleted');
      setSchedules((prev) => prev.filter((s) => s.id !== schedule.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/schedules`, newSchedule);
      toast.success('Event scheduled successfully!');
      setShowAddDialog(false);
      loadSchedules();
      setNewSchedule({
        team_id: user.team_id,
        type: 'practice',
        title: '',
        date: '',
        location: '',
        opponent: '',
        notes: '',
        is_recurring: false,
        recurrence_pattern: 'weekly',
        recurrence_end_date: ''
      });
    } catch (error) {
      toast.error('Failed to schedule event');
    }
  };

  const handleScheduleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please upload a CSV or Excel (.xlsx) file');
      return;
    }

    // Confirm if clearing schedule
    if (clearSchedule && !window.confirm('This will DELETE all existing schedule events before importing. Are you sure?')) {
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Dry-run to preview parsed rows
      const previewResp = await axios.post(
        `${API}/schedules/upload?team_id=${user.team_id}&update_existing=${updateExisting}&clear_schedule=${clearSchedule}&dry_run=true`,
        formData
      );

      const parsedRows = previewResp.data.rows || [];
      if (parsedRows.length === 0) {
        toast.warning('No rows parsed from the file. Check that your columns match the expected fields.');
        setUploadResult({ success: false, error: 'No rows parsed' });
      } else {
        setImportPreviewRows(parsedRows.map((r) => ({ ...r, date: toUsDate(r.date), time: to12h(r.time) })));
        setImportSourceHeaders(previewResp.data.source_headers || []);
        setImportRawRows(previewResp.data.raw_rows || []);
        setShowImportReview(true);
        setShowUploadDialog(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload schedule');
      setUploadResult({ success: false, error: error.response?.data?.detail || 'Upload failed' });
    }

    setIsUploading(false);
    e.target.value = '';
  };

  const confirmScheduleImport = async (rows) => {
    setIsImporting(true);
    try {
      const resp = await axios.post(`${API}/schedules/import`, {
        team_id: user.team_id,
        rows,
        update_existing: updateExisting,
        clear_schedule: clearSchedule,
      });
      toast.success(resp.data.message);
      setUploadResult(resp.data);
      setShowImportReview(false);
      setImportPreviewRows([]);
      loadSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import schedule');
    } finally {
      setIsImporting(false);
    }
  };

  const handleExportScheduleXlsx = async () => {
    try {
      const response = await axios.get(`${API}/schedules/export.xlsx?team_id=${user.team_id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'schedule.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export schedule');
    }
  };

  const downloadTemplate = async (format = 'csv') => {
    try {
      const response = await axios.get(`${API}/schedules/template?format=${format}`);
      
      if (format === 'excel' && response.data.encoding === 'base64') {
        // Handle Excel file (base64 encoded)
        const binaryString = atob(response.data.template);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Handle CSV file
        const blob = new Blob([response.data.template], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      toast.success(`${format.toUpperCase()} template downloaded!`);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleExportSchedule = async (format) => {
    try {
      const response = await axios.get(`${API}/schedules/export?team_id=${user.team_id}&format=${format}`);
      
      if (format === 'csv') {
        const blob = new Blob([response.data.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'schedule.csv';
        a.click();
        toast.success('Schedule exported as CSV');
      } else {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'schedule.json';
        a.click();
        toast.success('Schedule exported as JSON');
      }
    } catch (error) {
      toast.error('Failed to export schedule');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="schedule-page">
        {!canView('schedule') ? (
          <NoAccessScreen area="Schedule" />
        ) : (
        <>
        {isReadOnly('schedule') && <ReadOnlyBanner area="Schedule" />}
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Team Schedule
            </h1>
            <p className="text-muted-foreground mt-2">Manage practices, games, and events</p>
          </div>
          {user.role !== 'player' && canEdit('schedule') && (
            <div className="flex flex-col gap-2">
              {/* Upload Schedule Dialog */}
              <Dialog open={showUploadDialog} onOpenChange={(open) => { setShowUploadDialog(open); if (!open) setUploadResult(null); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="upload-schedule-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    UPLOAD SCHEDULE
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                      <FileSpreadsheet className="h-6 w-6" />
                      Import Schedule from CSV or Excel
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-muted/50 p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">Instructions:</h4>
                      <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                        <li>Download the CSV or Excel template below</li>
                        <li>Fill in event information (Title & Date required)</li>
                        <li>Save the file and upload it here</li>
                      </ol>
                    </div>

                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => downloadTemplate('csv')}
                      data-testid="download-schedule-csv-template-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download CSV Template
                    </Button>

                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => downloadTemplate('excel')}
                      data-testid="download-schedule-excel-template-btn"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Download Excel Template
                    </Button>

                    {/* Upload Options */}
                    <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                      <Label className="text-sm font-medium">Upload Options</Label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="update-existing-schedule"
                          checked={updateExisting}
                          onChange={(e) => setUpdateExisting(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor="update-existing-schedule" className="text-sm">
                          Update existing events (instead of skipping duplicates)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="clear-schedule"
                          checked={clearSchedule}
                          onChange={(e) => setClearSchedule(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor="clear-schedule" className="text-sm text-destructive">
                          Clear entire schedule before import (⚠️ deletes all events)
                        </label>
                      </div>
                    </div>

                    <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleScheduleUpload}
                        className="hidden"
                        id="schedule-upload"
                        disabled={isUploading}
                        data-testid="schedule-file-input"
                      />
                      <label 
                        htmlFor="schedule-upload" 
                        className={`cursor-pointer flex flex-col items-center gap-2 ${isUploading ? 'opacity-50' : ''}`}
                      >
                        {isUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            <span className="text-sm text-muted-foreground">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 text-muted-foreground" />
                            <span className="text-sm font-medium">Click to upload CSV or Excel file</span>
                            <span className="text-xs text-muted-foreground">Supports .csv, .xlsx, .xls</span>
                          </>
                        )}
                      </label>
                    </div>

                    {/* Upload Results */}
                    {uploadResult && (
                      <div className={`p-4 rounded-lg border ${uploadResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-start gap-2">
                          {uploadResult.success ? (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            {uploadResult.success ? (
                              <>
                                <p className="font-medium text-green-800">{uploadResult.message}</p>
                                <div className="text-sm text-green-700 mt-1">
                                  <span className="mr-4">Imported: {uploadResult.imported}</span>
                                  {uploadResult.updated > 0 && <span className="mr-4">Updated: {uploadResult.updated}</span>}
                                  {uploadResult.skipped > 0 && <span>Skipped: {uploadResult.skipped}</span>}
                                </div>
                                {uploadResult.errors?.length > 0 && (
                                  <div className="mt-2 text-xs text-amber-700">
                                    <p className="font-medium">Details:</p>
                                    {uploadResult.errors.slice(0, 5).map((err, i) => (
                                      <p key={i}>{err}</p>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-red-800">{uploadResult.error || 'Upload failed'}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <Button 
                      variant="ghost" 
                      className="w-full" 
                      onClick={() => {
                        setShowUploadDialog(false);
                        setUploadResult(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Add Schedule Dialog */}
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-schedule-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    SCHEDULE EVENT
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">Schedule New Event</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSchedule} className="space-y-4" data-testid="add-schedule-form">
                  <div>
                    <Label>Event Type</Label>
                    <Select
                      value={newSchedule.type}
                      onValueChange={(value) => setNewSchedule({...newSchedule, type: value})}
                    >
                      <SelectTrigger data-testid="schedule-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="practice">Practice</SelectItem>
                        <SelectItem value="game">Game</SelectItem>
                        <SelectItem value="volunteer">Volunteer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      data-testid="schedule-title"
                      value={newSchedule.title}
                      onChange={(e) => setNewSchedule({...newSchedule, title: e.target.value})}
                      placeholder="Team Practice / vs. Opponent"
                      required
                    />
                  </div>
                  <div>
                    <Label>Date & Time</Label>
                    <Input
                      type="datetime-local"
                      data-testid="schedule-date"
                      value={newSchedule.date}
                      onChange={(e) => setNewSchedule({...newSchedule, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      data-testid="schedule-location"
                      value={newSchedule.location}
                      onChange={(e) => setNewSchedule({...newSchedule, location: e.target.value})}
                      placeholder="Gym Address"
                      required
                    />
                  </div>
                  {newSchedule.type === 'game' && (
                    <div>
                      <Label>Opponent</Label>
                      <Input
                        data-testid="schedule-opponent"
                        value={newSchedule.opponent}
                        onChange={(e) => setNewSchedule({...newSchedule, opponent: e.target.value})}
                        placeholder="Opponent Team Name"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      data-testid="schedule-notes"
                      value={newSchedule.notes}
                      onChange={(e) => setNewSchedule({...newSchedule, notes: e.target.value})}
                      placeholder="Additional details..."
                    />
                  </div>
                  
                  {/* Recurring Event Options */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        id="recurring"
                        checked={newSchedule.is_recurring}
                        onChange={(e) => setNewSchedule({...newSchedule, is_recurring: e.target.checked})}
                        data-testid="recurring-checkbox"
                      />
                      <Label htmlFor="recurring" className="font-semibold">Make this a recurring event</Label>
                    </div>
                    
                    {newSchedule.is_recurring && (
                      <div className="space-y-4 pl-6 border-l-2 border-primary">
                        <div>
                          <Label>Recurrence Pattern</Label>
                          <Select
                            value={newSchedule.recurrence_pattern}
                            onValueChange={(value) => setNewSchedule({...newSchedule, recurrence_pattern: value})}
                          >
                            <SelectTrigger data-testid="recurrence-pattern">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="biweekly">Bi-weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Recurrence End Date</Label>
                          <Input
                            type="date"
                            data-testid="recurrence-end-date"
                            value={newSchedule.recurrence_end_date}
                            onChange={(e) => setNewSchedule({...newSchedule, recurrence_end_date: e.target.value})}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button type="submit" className="w-full" data-testid="submit-schedule-btn">
                    SCHEDULE EVENT
                  </Button>
                </form>
              </DialogContent>
              </Dialog>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExportSchedule('csv')} data-testid="export-schedule-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  EXPORT CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportScheduleXlsx} data-testid="export-schedule-xlsx-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  EXPORT EXCEL
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExportSchedule('json')} data-testid="export-schedule-json-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  EXPORT JSON
                </Button>
              </div>
            </div>
          )}
        </div>

        {schedules.length === 0 ? (
          <Card className="border-2">
            <CardContent className="p-12 text-center">
              <CalendarIcon className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
              <h3 className="font-heading text-2xl mt-4">No Events Scheduled</h3>
              <p className="text-muted-foreground mt-2">Schedule your first practice or game</p>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="list" className="space-y-6">
            <TabsList className="bg-muted">
              <TabsTrigger value="list" data-testid="list-view-tab">
                <List className="h-4 w-4 mr-2" />
                List View
              </TabsTrigger>
              <TabsTrigger value="calendar" data-testid="calendar-view-tab">
                <Grid className="h-4 w-4 mr-2" />
                Calendar View
              </TabsTrigger>
            </TabsList>

            <TabsContent value="list" className="space-y-4">
              {schedules.map((schedule) => (
                <Card key={schedule.id} className="border-2 hover:border-primary/50 transition-colors" data-testid={`schedule-card-${schedule.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-sm text-xs font-bold uppercase ${schedule.type === 'game' ? 'bg-accent text-accent-foreground' : 'bg-primary text-primary-foreground'}`}>
                            {schedule.type}
                          </span>
                          <h3 className="font-heading text-2xl font-bold uppercase">{schedule.title}</h3>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-muted-foreground mt-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span data-testid={`schedule-when-${schedule.id}`}>{fmtWhen(schedule)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>{schedule.location}</span>
                          </div>
                        </div>
                        {schedule.opponent && (
                          <p className="text-sm mt-2"><strong>Opponent:</strong> {schedule.opponent}</p>
                        )}
                        {schedule.notes && (
                          <p className="text-sm text-muted-foreground mt-2">{schedule.notes}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Button variant="outline" data-testid={`view-schedule-${schedule.id}`}>VIEW DETAILS</Button>
                        {user.role !== 'player' && canEdit('schedule') && (
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-schedule-${schedule.id}`}
                                  onClick={() => deleteSchedule(schedule)}>
                            <Trash2 className="h-4 w-4 mr-1" /> Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="calendar">
              <Card className="border-2">
                <CardHeader className="border-b bg-muted/30">
                  <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <CardTitle className="font-heading text-2xl uppercase">
                      {format(currentMonth, 'MMMM yyyy')}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-sm font-semibold text-muted-foreground py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const monthStart = startOfMonth(currentMonth);
                      const monthEnd = endOfMonth(currentMonth);
                      const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                      
                      // Add padding for days before month starts
                      const paddingDays = monthStart.getDay();
                      const allDays = [...Array(paddingDays).fill(null), ...days];
                      
                      return allDays.map((day, idx) => {
                        if (!day) {
                          return <div key={`empty-${idx}`} className="min-h-[80px]" />;
                        }
                        
                        const dayEvents = schedules.filter(s => {
                          try {
                            const scheduleDate = parseISO(s.date);
                            return isValid(scheduleDate) && isSameDay(scheduleDate, day);
                          } catch {
                            return false;
                          }
                        });
                        
                        const isToday = isSameDay(day, new Date());
                        
                        return (
                          <div 
                            key={day.toISOString()} 
                            className={`min-h-[80px] border rounded p-1 ${isToday ? 'bg-primary/10 border-primary' : 'bg-muted/20'}`}
                          >
                            <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-primary' : ''}`}>
                              {format(day, 'd')}
                            </div>
                            <div className="space-y-1">
                              {dayEvents.slice(0, 3).map(event => (
                                <div 
                                  key={event.id} 
                                  className={`text-[10px] p-1 rounded truncate ${
                                    event.type === 'game' 
                                      ? 'bg-accent text-accent-foreground' 
                                      : event.type === 'practice'
                                      ? 'bg-primary text-primary-foreground'
                                      : 'bg-emerald-500 text-white'
                                  }`}
                                  title={`${event.title} - ${event.location}`}
                                >
                                  {event.title}
                                </div>
                              ))}
                              {dayEvents.length > 3 && (
                                <div className="text-[10px] text-muted-foreground text-center">
                                  +{dayEvents.length - 3} more
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  
                  {/* Legend */}
                  <div className="flex gap-4 mt-4 justify-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-accent" />
                      <span>Game</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-primary" />
                      <span>Practice</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-emerald-500" />
                      <span>Volunteer</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
        </>
        )}

        <EditableImportReview
          open={showImportReview}
          onClose={() => setShowImportReview(false)}
          title="Review Schedule Import"
          columns={[
            { key: 'title', label: 'Title' },
            { key: 'type', label: 'Type' },
            { key: 'date', label: 'Date (MM/DD/YYYY)' },
            { key: 'time', label: 'Time (hh:mm AM/PM)' },
            { key: 'location', label: 'Location' },
            { key: 'opponent', label: 'Opponent' },
            { key: 'notes', label: 'Notes' },
          ]}
          initialRows={importPreviewRows}
          sourceHeaders={importSourceHeaders}
          rawRows={importRawRows}
          onConfirm={confirmScheduleImport}
          onSaveMappings={async (mappings) => {
            await axios.post(`${API}/teams/${user.team_id}/import-mappings`, { target: 'schedules', mappings });
            toast.success('Mapping remembered for future uploads');
          }}
          isImporting={isImporting}
        />
      </div>
    </Layout>
  );
}